/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import { type FunctionComponent } from 'react'
import moment from 'moment'

import { EuiFlexGroup, EuiFlexItem, EuiPanel, EuiText } from '@elastic/eui'

import CostTrendsChart from './CostTrendsChart'
import Title from './Title'

import type { BudgetConfigurationValues } from '../../../types'

const MONTHS_TO_DISPLAY = 6

const getPanelStyles = (isSmallScreen: boolean) =>
  css({
    position: 'sticky',
    top: '100px',
    ...(isSmallScreen
      ? { width: '100%', height: '200px', minWidth: '100%', maxWidth: '100%' }
      : { width: '30%', height: '438px', minWidth: '350px', maxWidth: '35%' }),
  })

export type Props = {
  amount: BudgetConfigurationValues['amount']
  isSmallScreen: boolean
  scopeType: BudgetConfigurationValues['scopeType']
  scopeValues: BudgetConfigurationValues['scopeValues']
}

const CostTrends: FunctionComponent<Props> = ({
  amount,
  isSmallScreen,
  scopeType,
  scopeValues,
}) => {
  const dateFrom = moment
    .utc()
    .subtract(MONTHS_TO_DISPLAY - 1, 'months')
    .startOf('month')
    .format()
  const dateTo = moment.utc().endOf('day').format()

  const dateFromFormatted = moment(dateFrom).format('MMMM D, YYYY')
  const dateToFormatted = moment(dateTo).format('MMMM D, YYYY')

  const isScopeSelected =
    scopeType === 'organization' || (scopeType === 'cloud_resource' && !!scopeValues?.length)

  return (
    <EuiPanel css={getPanelStyles(isSmallScreen)} hasShadow={false} hasBorder={true}>
      <EuiFlexGroup direction='column' gutterSize='m' css={{ height: '100%' }}>
        <EuiFlexItem grow={false}>
          <Title scopeType={scopeType} />
          {isScopeSelected && (
            <EuiText size='xs' color='subdued'>
              {dateFromFormatted} - {dateToFormatted}
            </EuiText>
          )}
        </EuiFlexItem>

        <CostTrendsChart
          amount={amount}
          dateFrom={dateFrom}
          dateTo={dateTo}
          isScopeSelected={isScopeSelected}
          scopeType={scopeType}
          scopeValues={scopeValues}
        />
      </EuiFlexGroup>
    </EuiPanel>
  )
}

export default CostTrends
